import { DEALER_ADMIN } from 'src/utils/enums/user-store-actions.enum';
import { DealerManagementService } from 'src/services/dealer-management.service';
import FooterPrivacy from '../../../../auth/components/baseTemplate/FooterPrivacy';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import { IUser } from 'src/utils/interfaces/user';
import Spinner from 'src/components/ui/spinner';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';

import { NUMBER_ZERO } from 'src/utils/constants';
import { Table } from './components/table';
import TableFooter from 'src/components/table/pagination/tableFooter';
import { useLocalization } from 'src/utils/useLocalization';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';

import ISMAddNewUser from '../add-new-user';
import ISMUpdateUser from '../update-user';

export enum Types {
    UserId = 'userId',
    LastName = 'lastName',
}

interface IPropsTableItem {
    FIRSTNAME: string;
    LASTNAME: string;
    LOGIN: string;
    PARTYID: number;
    PERMISSIONEXISTS: number;
    STATUS: number;
    DEALERIDS: string;
    STATUSTEXT: string;
    ID: string;
}

interface IUserSearchLocationState {
    addUser?: boolean;
    searchType?: string;
    searchValue?: string;
    addUserStatus?: 'success' | 'error';
}

const UserSearch = () => {
    const location = useLocation();
    const locationState = (location.state || {}) as IUserSearchLocationState;
    const [displayedContent, setDisplayedContent] = useState<IPropsTableItem[]>(
        [],
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isAdding, setIsAdding] = useState<boolean>(!!locationState.addUser);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [userNameEdit, setUserNameEdit] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean>(
        locationState.addUserStatus === 'success',
    );
    const [isSuccessDeactivate, setIsSuccessDeactivate] =
        useState<boolean>(false);
    const [isSuccessActivate, setIsSuccessActivate] = useState<boolean>(false);
    const [isSuccessDeleted, setIsSuccessDeleted] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(
        locationState.addUserStatus === 'error',
    );
    const [isErrorDGM, setIsErrorISA] = useState<boolean>(false);
    const [brand, setBrand] = useState<string>('vw6');

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { dealerMgmt, addNewUser, editUser } = useLocalization();

    const dsmSearch = useSelector((reduxState: IUser) => reduxState.dsmSearch);
    const currentDealers = useSelector(
        (reduxState: IUser) => reduxState.currentDealers,
    );
    const dealerDataInfo = useSelector(
        (reduxState: IUser) => reduxState.dealerDataInfo,
    );

    const buttonClasses = useMemo(() => `c-btn ${brand} u-ml-small`, [brand]);

    const getDealersForISM = useCallback(async () => {
        setIsLoading(true);
        const service = new DealerManagementService();
        try {
            const res = await service.getDealersForISM(
                dsmSearch.searchType,
                dsmSearch.searchValue,
            );

            if (res?.data) {
                dispatch({
                    type: DEALER_ADMIN.DEALER_ADMIN_USER_SEARCH,
                    payload: {
                        searchType: dsmSearch.searchType,
                        searchValue: dsmSearch.searchValue,
                        users: res.data,
                    },
                });
            }
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, dsmSearch.searchType, dsmSearch.searchValue]);

    const getDealerGroups = useCallback(async () => {
        const service = new DealerManagementService();
        try {
            const res = await service.getDealerGroups(
                currentDealers?.default as string,
            );
            if (res?.data) {
                dispatch({
                    type: DEALER_ADMIN.SET_DEALER_GROUP_DATA,
                    payload: { dealerGroupState: res.data },
                });
            }
        } catch {
            setIsError(true);
        }
    }, [currentDealers?.default, dispatch]);

    useEffect(() => {
        if (
            !dsmSearch.users.length &&
            !locationState.addUser &&
            !locationState.addUserStatus
        ) {
            navigate('/dashboard/systemadmin/internal-security-maintenance');
        }
    }, []);

    useEffect(() => {
        if (locationState.addUserStatus) {
            setIsAdding(false);
            setIsSuccess(locationState.addUserStatus === 'success');
            setIsError(locationState.addUserStatus === 'error');
        }
    }, [locationState.addUserStatus]);

    useEffect(() => {
        if (!currentDealers || !dealerDataInfo) return;

        const { dealer } = dealerDataInfo;
        const dealerBrand = getBrandByDealerNumber(
            dealer.brand,
            dealer.dealerNumber,
        );

        setBrand(dealerBrand);
        getDealerGroups();
    }, [currentDealers, dealerDataInfo]);

    useEffect(() => {
        if (!isSuccess) return;

        getDealersForISM();
    }, [getDealersForISM, isSuccess]);

    const handleClickItem = useCallback((userName: string) => {
        setIsSuccessDeleted(false);
        setUserNameEdit(userName);
        setIsEditing(true);
    }, []);

    const handlerEditBackclick = useCallback(
        (didEdit: boolean, shouldLoadList?: boolean, didDelete?: boolean) => {
            setIsEditing(didEdit);
            setIsSuccessDeleted(!!didDelete);

            if (shouldLoadList) {
                setIsLoading(true);
                getDealersForISM();
            }
        },
        [getDealersForISM],
    );

    if (isAdding) {
        const emailFromState =
            locationState?.searchType === Types.UserId
                ? locationState?.searchValue ?? ''
                : '';
        return (
            <ISMAddNewUser
                prefillEmail={
                    emailFromState ||
                    (dsmSearch.searchType === Types.UserId
                        ? dsmSearch.searchValue
                        : '')
                }
            />
        );
    }

    if (isEditing && !isLoading) {
        return (
            <ISMUpdateUser
                backClick={handlerEditBackclick}
                username={userNameEdit}
                setErrorNotification={setIsError}
            />
        );
    }

    return (
        <div className='user-search-ism'>
            {isSuccess && (
                <div className='success-notification'>
                    <Notification
                        content={addNewUser.NOTIFICATION_SUCCESS}
                        type={NotificationType.SUCCESS}
                        visibility={isSuccess}
                        setVisibility={setIsSuccess}
                    />
                </div>
            )}

            {isSuccessDeactivate && (
                <div className='success-notification'>
                    <Notification
                        content='The user has been suspended.'
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessDeactivate}
                        setVisibility={setIsSuccessDeactivate}
                    />
                </div>
            )}

            {isSuccessActivate && (
                <div className='success-notification'>
                    <Notification
                        content='The user has been reinstated.'
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessActivate}
                        setVisibility={setIsSuccessActivate}
                    />
                </div>
            )}

            {isSuccessDeleted && (
                <div className='success-notification'>
                    <Notification
                        content='User record has been deleted successfully.'
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessDeleted}
                        setVisibility={setIsSuccessDeleted}
                        extraClass='delete-success-left'
                    />
                </div>
            )}

            {isError && (
                <div className='success-notification'>
                    <Notification
                        content={editUser.NOTIFICATION_ERROR_REQUEST}
                        type={NotificationType.ERROR}
                        visibility={isError}
                        setVisibility={setIsError}
                    />
                </div>
            )}

            {isErrorDGM && (
                <div className='success-notification'>
                    <Notification
                        content='Something went wrong. Please try again later.'
                        type={NotificationType.ERROR}
                        visibility={isErrorDGM}
                        setVisibility={setIsErrorISA}
                    />
                </div>
            )}

            <div className='page-title'>
                <h5>User Search Results</h5>
            </div>

            <div
                className='page-title'
                style={{
                    alignItems: 'center',
                }}
            >
                <div>
                    <span
                        style={{
                            color: '#777777',
                        }}
                    >
                        {'< '}
                    </span>
                    <Link to='/dashboard/systemadmin/internal-security-maintenance'>
                        <span>New Search</span>
                    </Link>
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                    }}
                >
                    <button
                        className={buttonClasses}
                        onClick={() => setIsAdding(true)}
                    >
                        <span className='c-btn__text'>
                            {dealerMgmt.DEALER_MGMT_ADD_BUTTON_LABEL}
                        </span>
                    </button>
                </div>
            </div>

            <div className={`main-container ${isLoading ? 'no-content' : ''}`}>
                {isLoading ? (
                    <div className='loader_container'>
                        <Spinner />
                    </div>
                ) : dsmSearch?.users?.length > NUMBER_ZERO ? (
                    <>
                        <Table
                            items={displayedContent}
                            onClick={handleClickItem}
                            updateData={getDealersForISM}
                            setIsSuccessActivate={setIsSuccessActivate}
                            setIsSuccessDeactivate={setIsSuccessDeactivate}
                            setIsSuccessDeleted={setIsSuccessDeleted}
                            setIsError={setIsError}
                            setIsErrorISA={setIsErrorISA}
                        />

                        <TableFooter
                            brand={brand}
                            content={dsmSearch.users}
                            setDisplayedContent={setDisplayedContent}
                        />
                    </>
                ) : (
                    <div className='no-content no-results'>
                        No results found. Please verify your search criteria and
                        try again.
                    </div>
                )}
            </div>

            <div style={{ gridColumnStart: 2, marginTop: '40px' }}>
                <FooterPrivacy />
            </div>
        </div>
    );
};

export default UserSearch;
